import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { generateId, formatCurrency, MEMBER_COLOURS } from '../utils/helpers';
import { fetchAllowedEmails } from '../utils/storage';

export default function MembersManager({ isOpen, onClose, members, onSave, holidays }) {
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [allowedEmails, setAllowedEmails] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchAllowedEmails().then(setAllowedEmails);
    }
  }, [isOpen]);

  const addMember = () => {
    if (!newName.trim()) return;
    const colour = MEMBER_COLOURS[members.length % MEMBER_COLOURS.length];
    onSave([...members, { id: generateId(), name: newName.trim(), colour, splitCosts: true }]);
    setNewName('');
  };

  const removeMember = (id) => {
    if (window.confirm('Remove this family member? They will also be removed from any trips.')) {
      onSave(members.filter(m => m.id !== id));
    }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditName(m.name);
  };

  const saveEdit = () => {
    if (!editName.trim()) return;
    onSave(members.map(m => m.id === editingId ? { ...m, name: editName.trim() } : m));
    setEditingId(null);
    setEditName('');
  };

  // Per-person spending summary
  const getMemberSpending = (memberId) => {
    let total = 0;
    let paid = 0;
    holidays.forEach(h => {
      const memberCount = h.familyMembers.length || 1;
      h.expenses.forEach(exp => {
        if (exp.assignedTo === memberId) {
          total += exp.amount;
          if (exp.isPaid) paid += exp.amount;
        } else if (exp.assignedTo === 'shared' && h.familyMembers.includes(memberId)) {
          total += exp.amount / memberCount;
          if (exp.isPaid) paid += exp.amount / memberCount;
        }
      });
    });
    return { total, paid };
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Family Members">
      <div className="space-y-4">
        {/* Add new */}
        <div className="flex gap-2">
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addMember()}
            placeholder="Add family member..."
            className="flex-1 px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none"
          />
          <button
            onClick={addMember}
            className="px-4 py-2.5 bg-gradient-to-r from-purple-500 to-teal-500 text-white rounded-xl font-semibold hover:from-purple-600 hover:to-teal-600 transition-colors"
          >
            Add
          </button>
        </div>

        {/* Member list */}
        {members.length === 0 ? (
          <p className="text-center text-gray-400 py-4">No family members yet. Add someone above!</p>
        ) : (
          <div className="space-y-2">
            {members.map(m => {
              const spending = getMemberSpending(m.id);
              return (
                <div key={m.id} className="bg-gray-50 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: m.colour }}>
                    {m.name[0].toUpperCase()}
                  </div>

                  <div className="flex-1">
                    {editingId === m.id ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && saveEdit()}
                          className="px-3 py-1 border-2 border-purple-300 rounded-lg focus:outline-none text-sm"
                          autoFocus
                        />
                        <button onClick={saveEdit} className="text-sm text-purple-600 font-medium">Save</button>
                        <button onClick={() => setEditingId(null)} className="text-sm text-gray-400">Cancel</button>
                      </div>
                    ) : (
                      <>
                        <div className="font-semibold text-gray-700">{m.name}</div>
                        {m.email && (
                          <div className="text-xs text-purple-500">{m.email}</div>
                        )}
                        <div className="text-xs text-gray-500">
                          Total share: {formatCurrency(spending.total)} · Paid: {formatCurrency(spending.paid)}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <button
                            onClick={() => onSave(members.map(x => x.id === m.id ? { ...x, splitCosts: !x.splitCosts } : x))}
                            className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${
                              m.splitCosts !== false
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-gray-200 text-gray-500'
                            }`}
                          >
                            {m.splitCosts !== false ? '💷 Splits costs' : 'Not splitting costs'}
                          </button>
                          {allowedEmails.length > 0 && (
                            <select
                              value={m.email || ''}
                              onChange={e => onSave(members.map(x => x.id === m.id ? { ...x, email: e.target.value || undefined } : x))}
                              className="text-xs px-2 py-0.5 rounded-full border border-gray-200 bg-white text-gray-600 focus:outline-none focus:border-purple-400"
                            >
                              <option value="">Not linked</option>
                              {allowedEmails.map(email => (
                                <option key={email} value={email}>{email}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {editingId !== m.id && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => startEdit(m)}
                        className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-400 text-sm transition-colors"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => removeMember(m.id)}
                        className="p-1.5 rounded-lg hover:bg-red-100 text-gray-400 text-sm transition-colors"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
